import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import SpeedIcon from '../icons/SpeedIcon';
import HuddleVisionIcon from '../icons/HuddleVisionIcon';
import RedCircle from '../icons/RedCircleIcon';
import ListItem from '../atoms/ListItem';
import NamedBox from '../atoms/NamedBox';
import ProgressLine from '../atoms/ProgressLine';
import Tooltip from '../atoms/Tooltip';
import InGameRating from '../types/InGameRating';
import {
  COLOR_BLACK,
  COLOR_BLUE,
  COLOR_DARK_BLUE,
  COLOR_GREEN,
  COLOR_PURPLE,
  COLOR_TEXT,
  COLOR_WHITE,
} from '../styles/colors';
import InfoIcon from '../icons/InfoIcon';
import EllipsisIcon from '../icons/EllipsisIcon';
import { Grid } from '@material-ui/core';
import SpeedRecruitIcon from '../icons/SpeedRecruitIcon';
import VideoIcon from '@material-ui/icons/PlayCircleOutline';
import PffLinkIcon from '../icons/PffLinkIcon';
import DistanceIcon from '../icons/DistanceIcon';
import RunnerIcon from '../icons/RunnerIcon';
import ArrowUpCircleIcon from '../icons/ArrowUpCircleIcon';
import FilmIcon from '../icons/FilmIcon';
import LinkIcon from '../icons/LinkIcon';

interface PlayerInGameRatingProps {
  className?: string;
  inGameRating: InGameRating;
  label?: string;
  pffUltimateIcon?: boolean;
}

const BETTER_THAN_TOOLTIP_TEXT = 'Better than D-I position';

const useStyles = makeStyles((theme: Theme) => ({
  PlayerInGameRating: {},
  headerName: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    width: '100%',
    marginRight: theme.spacing(2),
    fontWeight: 700,
  },
  headerIcon: {
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '44px',
    height: '44px',
    marginRight: theme.spacing(1.5),
    borderRadius: '50%',
    border: `2px solid ${COLOR_BLACK}`,
    backgroundColor: COLOR_PURPLE,
    fontSize: theme.typography.pxToRem(14),
    color: COLOR_WHITE,
    
    [theme.breakpoints.down('md')]: {
      width: 34,
      height: 34,
      fontSize: theme.typography.pxToRem(12),
    },
  },
  headerText: {
    flexGrow: 1,
  },
  headerInfo: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
    maxWidth: '44%',
    color: COLOR_TEXT,

    '& svg': {
      display: 'block',
    }
  },
  headerInfoLogo: {
    maxWidth: '150px',
    height: 'auto',
  },
  headerInfoIcon: {
    width: 32,
    minWidth: 24,
  },
  gridContainer: {
    alignItems: 'center',
  },
  rowContent: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    justifyContent: 'space-between',
    
    [theme.breakpoints.down('md')]: {
      display: 'inline-flex',
    },
  },
  gridIcon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.spacing(1),
  },
  progressLineWrapper: {
    width: '100%',
  },
  progressLine: {
    maxWidth: 350,
    padding: 0,
    marginLeft: 'auto',
  },
  progressLineBar: {
    backgroundColor: COLOR_PURPLE,
  },
  listArrowIcon: {
    '& .arrow-icon-background path': {
      fill: 'transparent',
    },

    '& .arrow-icon-color path': {
      stroke: COLOR_BLACK,
    },
  },
  linkUrl: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    display: 'inline-block',
    maxWidth: 200,
    minWidth: 200,
    fontWeight: 700,
    color: COLOR_BLUE,

    '&:hover': {
      color: COLOR_DARK_BLUE,
      textDecoration: 'underline',
    },
  }
}), { name: PlayerInGameRating.name });

export default function PlayerInGameRating (props: PlayerInGameRatingProps) {
  const {
    className,
    inGameRating,
    label,
    pffUltimateIcon = false
  } = props;
  const classes = useStyles();
  const Icon:any = pffUltimateIcon ? PffLinkIcon :HuddleVisionIcon;

  if (!inGameRating) return null;

  function formatToTenth(value: number): string {
    // Format the number to one decimal place using toFixed(1)
    return value.toFixed(1);
  }

  function formatToHundredth(value: number): string {
    // Format the number to two decimal place using toFixed(1)
    return value.toFixed(2);
  }

  const noEvents = !(inGameRating?.MPH || inGameRating?.MPHPercentage || inGameRating?.timeToMax);
  if (noEvents) return null;
  const mphPercentageValue = inGameRating?.MPHPercentage && formatToTenth(inGameRating?.MPHPercentage);
  const mphConfidencePercentValue = inGameRating?.mphConfidencePercent && formatToTenth(inGameRating?.mphConfidencePercent);

  function isSpeedRecruitEnable (value: any):boolean {
    if (value >= 75) {
      return true;
    }
    return false;
  }

  return (
    <NamedBox
      className={clsx(classes.PlayerInGameRating, className)}
      headerNameClassName={classes.headerName}
      name={
        <>
          <div className={classes.headerIcon}>MPH</div>
          <span className={classes.headerText}>{label || 'In-Game Metrics'}</span>
        </>
      }
      headerContent={
        <a className={classes.headerInfo} href='https://huddlevision.ai' target='_blank' rel='noopener noreferrer'>
          <Icon className={clsx(classes.headerInfoLogo)} />

          <Tooltip title='All in-game and mph data created in collaboration with Huddlevision.ai and Tracking Football technology.'>
            <InfoIcon className={classes.headerInfoIcon} />
          </Tooltip>
        </a>
      }
    >
      {(inGameRating?.MPH && inGameRating?.MPHPercentage)
          ? <ListItem
              icon={SpeedIcon}
              // title={`Max Speed (MPH):`}
              title='Max Speed:'
              // extendWith={true}
            >
              <div className={classes.rowContent}>
                <b>{inGameRating?.MPH ? `${formatToHundredth(inGameRating?.MPH)} MPH` : 'N/A'}</b>
              </div>
              {/* <Tooltip title={BETTER_THAN_TOOLTIP_TEXT}>
                <div className={classes.progressLineWrapper}>
                  <ProgressLine
                    className={classes.progressLine}
                    value={inGameRating?.MPHPercentage}
                    showPercentileSign={false}
                  />
                </div>
              </Tooltip> */}
            </ListItem>
          : null
          }

          {/* {(inGameRating?.timeToMax)
          ? <ListItem
              icon={SpeedIcon}
              title='Time to Max Speed (Sec):'
              // extendWith={true}
            >
              <b>{formatToTenth(inGameRating?.timeToMax)}</b>
            </ListItem>
            : null
          } */}

          {(inGameRating?.distanceTraveled)
          ? <ListItem
              icon={DistanceIcon}
              title='Play Distance:'
              // extendWith={true}
            >
              <div className={classes.rowContent}>
                <b>{formatToTenth(inGameRating?.distanceTraveled)} yds.</b>
              </div>
            </ListItem>
            : null
          }

          {(inGameRating?.MPHPercentage)
          ? <ListItem
              icon={RunnerIcon}
              title='MPH Database:'
              // extendWith={true}
            >
              <Grid container className={classes.gridContainer}>
              {/* <Grid item lg={1} xs={2}></Grid>
              <Grid item lg={3} xs={5}>
              <b>{formatToTenth(inGameRating?.distanceTraveled)} yds.</b>
              </Grid> */}
                <Grid item lg={4} sm={5} xs={12}>
                  <div className={classes.rowContent}>
                    <b>{mphPercentageValue}%</b>

                    {isSpeedRecruitEnable(mphPercentageValue) && (
                      <div className={classes.gridIcon}>
                        <SpeedRecruitIcon className={'purple'} padding={'0'} />
                      </div>
                    )}
                  </div>
                </Grid>
                <Grid item lg xs>
                  <div className={classes.progressLineWrapper}>
                    <ProgressLine
                      className={classes.progressLine}
                      progressBarClassName={classes.progressLineBar}
                      value={inGameRating?.MPHPercentage}
                    />
                  </div>
                </Grid>
              </Grid>
            </ListItem>
            : null
          }

          {/* {(inGameRating?.mphConfidencePercent)
          ? <ListItem
              icon={<ArrowUpCircleIcon className="green" />}
              title='MPH Confidence:'
              // extendWith={true}
            >
              <div className={classes.rowContent}>
                <b>{mphConfidencePercentValue}%</b>
              </div>
            </ListItem>
            : null
          } */}

          {(!!mphConfidencePercentValue) &&
            <ListItem
            icon={ArrowUpCircleIcon}
            iconClassName={classes.listArrowIcon}
            title='MPH Confidence:'
          >
            <div className={classes.rowContent}>
              <b>{mphConfidencePercentValue}%</b>
            </div>
          </ListItem>
          }

          {(inGameRating?.year)
          ? <ListItem
              icon={FilmIcon}
              title='HS Film Year:'
              // extendWith={true}
            >
              <div className={classes.rowContent}>
                <b>{inGameRating?.year}</b>
              </div>
            </ListItem>
            : null
          }

          {(inGameRating?.filmUrl)
          ? <ListItem
              icon={LinkIcon}
              title='HS Film URL:'
              // extendWith={true}
            >
              <div className={classes.rowContent}>
                <a
                  className={classes.linkUrl}
                  href={inGameRating?.filmUrl}
                  target='_blank'
                  title={inGameRating?.filmUrl}
                >
                  {inGameRating?.filmUrl}
                  </a>
              </div>
            </ListItem>
            : null
          }
    </NamedBox>
  );
}
